import { getBranches } from "@aitisal/sdk/restaurant";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Twitter,
} from "lucide-react";
import SecondaryFooter from "@aitisal/ui/common/SecondaryFooter";
import { getTitle } from "../../helper/helper";
import { useOrders } from "../../stores/ordersStore";
import { useBrandingConfig } from "../../utils/brandingConfig";
import StoreLogo from "../retail/StoreLogo";

interface FooterProps {
  className?: string;
}

interface FooterLink {
  label: string;
  path?: string;
  href?: string;
}

const linkBase =
  "text-sm text-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_65%,transparent)] transition-colors duration-200 text-left w-fit";

const SOCIALS = [
  { key: "facebookLink", label: "Facebook", icon: Facebook, hover: "hover:bg-blue-600" },
  {
    key: "instagramLink",
    label: "Instagram",
    icon: Instagram,
    hover: "hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600",
  },
  { key: "twitterLink", label: "Twitter", icon: Twitter, hover: "hover:bg-sky-500" },
  { key: "linkedinLink", label: "LinkedIn", icon: Linkedin, hover: "hover:bg-blue-700" },
] as const;

export default function Footer({ className = "" }: FooterProps) {
  const navigate = useNavigate();
  const { orderIds } = useOrders();

  const brandingConfig = useBrandingConfig();
  const brandId = brandingConfig.brandId;

  const { data: branchesData } = useQuery({
    queryKey: ["branches-data-footer", brandId],
    queryFn: async () => {
      const response = await getBranches(brandId || "");
      return response?.data;
    },
    enabled: Boolean(brandId),
    staleTime: 5 * 60 * 1000,
  });

  const brandTitle = getTitle(brandingConfig.title) || "Store";
  const restaurant = branchesData?.items?.[0];
  const description = restaurant?.description
    ? getTitle(restaurant.description)
    : brandingConfig.description || "";
  const phone = restaurant?.phone || brandingConfig.phoneNumber || "";
  const email = restaurant?.email || "";
  const address = restaurant?.location
    ? `${restaurant.location.streetAddress || ""}, ${restaurant.location.city || ""}, ${restaurant.location.state || ""} ${restaurant.location.postalCode || ""}`.trim()
    : "";

  const socialMediaLinks: Partial<
    Record<"facebookLink" | "instagramLink" | "twitterLink" | "linkedinLink", string>
  > = brandingConfig.socialMedia || {};
  const hasSocials = Object.values(socialMediaLinks).some(Boolean);

  const contactUsHref = email
    ? `mailto:${email}`
    : phone
      ? `tel:${phone}`
      : undefined;

  const footerLinks: { heading: string; links: FooterLink[] }[] = [
    {
      heading: "Support",
      links: [
        { label: "Contact Us", href: contactUsHref },
        { label: "Track Order", path: "/c/ordertracking" },
      ],
    },
    {
      heading: "Company",
      links: [{ label: "Privacy Policy", path: "/c/privacy-policy" }],
    },
  ];

  const navigateToPath = (path: string) => {
    if (window.location.pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    let target = path;
    if (path === "/c/ordertracking" && orderIds.length > 0) {
      target = `${path}?orderId=${encodeURIComponent(orderIds[orderIds.length - 1])}`;
    }
    navigate({ to: target as never });
  };

  const renderLink = (link: FooterLink) => {
    if (!link.path && !link.href) {
      return (
        <span
          aria-disabled="true"
          className={`${linkBase} cursor-default text-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_45%,transparent)]`}
        >
          {link.label}
        </span>
      );
    }
    if (link.href) {
      return (
        <a href={link.href} className={`${linkBase} hover:text-[var(--footer-text-color,#ffffff)]`}>
          {link.label}
        </a>
      );
    }
    return (
      <button
        type="button"
        onClick={() => navigateToPath(link.path!)}
        className={`${linkBase} cursor-pointer hover:text-[var(--footer-text-color,#ffffff)]`}
      >
        {link.label}
      </button>
    );
  };

  return (
    <footer
      className={className}
      style={{
        backgroundColor: "var(--footer-background-color, #111827)",
        color: "var(--footer-text-color, #ffffff)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-12 md:gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-6">
            <div className="flex justify-start">
              <StoreLogo
                src={brandingConfig.logo}
                title={brandTitle}
                className="h-12 w-auto max-w-[200px]"
              />
            </div>

            {description && (
              <p className="mt-5 max-w-md text-sm leading-relaxed text-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_65%,transparent)]">
                {description}
              </p>
            )}

            <div className="mt-6 space-y-3">
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="flex w-fit items-center gap-2.5 text-sm text-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_65%,transparent)] transition-colors hover:text-[var(--footer-text-color,#ffffff)]"
                >
                  <Phone className="size-4 shrink-0" />
                  <span>{phone}</span>
                </a>
              )}
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="flex w-fit items-center gap-2.5 text-sm text-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_65%,transparent)] transition-colors hover:text-[var(--footer-text-color,#ffffff)]"
                >
                  <Mail className="size-4 shrink-0" />
                  <span className="break-all">{email}</span>
                </a>
              )}
              {address && address !== "," && (
                <div className="flex items-start gap-2.5 text-sm text-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_65%,transparent)]">
                  <MapPin className="size-4 shrink-0 mt-0.5" />
                  <span className="break-words">{address}</span>
                </div>
              )}
            </div>

            {hasSocials && (
              <div className="mt-6 flex items-center gap-3">
                {SOCIALS.map(({ key, label, icon: Icon, hover }) =>
                  socialMediaLinks[key] ? (
                    <a
                      key={key}
                      href={socialMediaLinks[key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className={`flex size-10 items-center justify-center rounded-full border border-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_25%,transparent)] bg-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_10%,transparent)] transition-all duration-300 hover:scale-110 ${hover}`}
                    >
                      <Icon className="size-5 text-white" strokeWidth={1.5} />
                    </a>
                  ) : null,
                )}
              </div>
            )}
          </div>

          {/* Link Columns */}
          {footerLinks.map((column) => (
            <div
              key={column.heading}
              className="col-span-1 md:col-span-3"
            >
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_75%,transparent)] mb-4">
                {column.heading}
              </h4>
              <ul className="space-y-3">
                {column.links.map((link) => (
                  <li key={link.label}>{renderLink(link)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 border-t border-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_20%,transparent)] pt-8">
          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_55%,transparent)]">
              © {new Date().getFullYear()} {brandTitle}. All rights reserved.
            </p>
            <button
              type="button"
              onClick={() => navigateToPath("/c/privacy-policy")}
              className="text-sm text-[color-mix(in_srgb,var(--footer-text-color,#ffffff)_65%,transparent)] transition-colors hover:text-[var(--footer-text-color,#ffffff)] cursor-pointer"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>

      <SecondaryFooter />
    </footer>
  );
}
